import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, WhatsappLogo, ChatCircle, MapPin, InstagramLogo } from '@phosphor-icons/react';
import { LOGO_SRC } from '../constants/branding';
import { CONTACT } from '../constants/contact';

const footerLinks = [
  { label: 'Početna', path: '/' },
  { label: 'Usluge', path: '/usluge' },
  { label: 'O nama', path: '/o-nama' },
  { label: 'Projekti', path: '/projekti' },
  { label: 'Kontakt', path: '/kontakt' },
];

const services = [
  'Klimatizacija',
  'Grejanje',
  'Ventilacija',
  'Servis i održavanje',
  'Konsultacije',
];

export function Footer() {
  const location = useLocation();
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-white text-primary border-t border-primary/10" data-testid="main-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          <div className="lg:col-span-1">
            <Link
              to="/"
              onClick={() => {
                if (location.pathname === '/') window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-3 mb-6"
              data-testid="footer-logo-link"
            >
              <img
                src={LOGO_SRC}
                alt="THERMO COOL SYSTEM logo"
                className="h-14 w-14 object-contain rounded-xl p-1 border border-primary/10 bg-surface/50"
              />
              <div className="leading-none">
                <span className="font-brand font-semibold text-base text-primary tracking-[-0.04em] block">
                  THERMO COOL
                </span>
                <span className="font-brand font-medium text-[0.625rem] text-muted tracking-[0.22em] uppercase mt-1 block">
                  SYSTEM
                </span>
              </div>
            </Link>
            <p className="text-muted text-sm leading-relaxed mb-6">
              Energija koja štedi, komfor koji traje. Vaš pouzdan partner za klimatizaciju, grejanje i ventilaciju.
            </p>

            <div className="flex gap-3">
              <a
                href={CONTACT.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-surface border border-primary/10 text-primary hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-white hover:border-transparent rounded-lg flex items-center justify-center transition-all duration-300"
                data-testid="footer-instagram-link"
              >
                <InstagramLogo size={20} weight="fill" />
              </a>
              <a
                href={CONTACT.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 bg-surface border border-primary/10 text-primary hover:bg-green-500 hover:text-white hover:border-transparent rounded-lg flex items-center justify-center transition-all duration-300"
                data-testid="footer-whatsapp-link"
              >
                <WhatsappLogo size={20} weight="fill" />
              </a>
              <a
                href={CONTACT.viber}
                className="w-10 h-10 bg-surface border border-primary/10 text-primary hover:bg-purple-600 hover:text-white hover:border-transparent rounded-lg flex items-center justify-center transition-all duration-300"
                data-testid="footer-viber-link"
              >
                <ChatCircle size={20} weight="fill" />
              </a>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-primary">Navigacija</h4>
            <ul className="space-y-3">
              {footerLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    onClick={() => {
                      if (location.pathname === link.path) {
                        window.scrollTo({ top: 0, behavior: 'smooth' });
                      }
                    }}
                    className="text-muted hover:text-primary hover:translate-x-1 inline-block transition-all duration-300"
                    data-testid={`footer-link-${link.path.replace('/', '') || 'home'}`}
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-primary">Usluge</h4>
            <ul className="space-y-3">
              {services.map((service, i) => (
                <li key={i} className="text-muted">
                  {service}
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-bold text-lg mb-6 text-primary">Kontakt</h4>
            <ul className="space-y-4">
              <li>
                <div
                  className="flex items-start gap-3 text-muted cursor-default select-text"
                  data-testid="footer-phone-display"
                >
                  <Phone size={20} weight="duotone" className="text-cool mt-0.5 flex-shrink-0" />
                  <span>{CONTACT.phoneDisplay}</span>
                </div>
              </li>
              <li>
                <a
                  href={CONTACT.whatsapp}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-3 text-muted hover:text-primary transition-colors"
                  data-testid="footer-whatsapp-text-link"
                >
                  <WhatsappLogo size={20} weight="duotone" className="text-green-600 mt-0.5 flex-shrink-0" />
                  <span>WhatsApp</span>
                </a>
              </li>
              <li>
                <a
                  href={CONTACT.viber}
                  className="flex items-start gap-3 text-muted hover:text-primary transition-colors"
                  data-testid="footer-viber-text-link"
                >
                  <ChatCircle size={20} weight="duotone" className="text-purple-600 mt-0.5 flex-shrink-0" />
                  <span>Viber</span>
                </a>
              </li>
              <li className="flex items-start gap-3 text-muted">
                <MapPin size={20} weight="duotone" className="text-heat mt-0.5 flex-shrink-0" />
                <address className="not-italic" data-testid="footer-address">
                  {CONTACT.address}
                </address>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-primary/10 bg-surface/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-muted text-sm" data-testid="footer-copyright">
              © {currentYear} Powered by Limtech Solutions
            </p>
            <p className="text-muted/80 text-xs font-medium tracking-wider">
              SINCE 2022
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
